'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDecoratedSlotContent } from '../../hooks/use-decorated-slot-content';
import { useInertProps } from '../../hooks/use-inert-props';
import { Slot } from '../slot';
import { AccordionItemContext } from './context/accordion-item.context';
import { accordionPanelRecipe, accordionPanelStyles } from './styles/accordion-panel.styles';
import type { AccordionPanelPropsInterface } from './types/accordion-panel.types';

/**
 * Contains the content that is hidden and revealed by its sibling
 * `Accordion.Header`.
 *
 * @since 8.43.0
 */

export const AccordionPanel = forwardRef<HTMLDivElement, AccordionPanelPropsInterface>(
  (props: AccordionPanelPropsInterface, forwardedRef) => {
    const { asChild = false, children, className, css: cssProp, style, ...rest } = props;

    const { expanded, value } = useContext(AccordionItemContext);

    const panelStyles = css(accordionPanelRecipe.raw({ open: expanded }));
    const styles = css(accordionPanelStyles, cssProp);

    const Component = asChild ? Slot : 'div';

    const content = useDecoratedSlotContent({
      children,
      asChild,
      template: (childContent) => (
        <div className={cx('c11n', styles, className)} style={{ ...style }}>
          {childContent}
        </div>
      ),
      dependencies: [styles, className, style],
    });

    const inertProps = useInertProps(!expanded);

    return (
      <Component
        ref={forwardedRef}
        id={`${value}-panel`}
        data-c11n-component="Accordion.Panel"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', panelStyles)}
        {...inertProps}
        {...rest}
      >
        {content}
      </Component>
    );
  },
);

AccordionPanel.displayName = 'Accordion.Panel';
